import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { usePlatformRoleContext } from '@/hooks/usePlatformRoleContext'
import { supabase } from '@/lib/supabaseClient'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

function getAccountTypeLabel(role: string | null): string {
  if (role === 'platform_admin') return 'Platform Admin'
  if (role === 'platform_support') return 'Platform Support'
  return 'Client'
}

export default function PortalSettings() {
  const { user, updatePassword, updateUserMetadata } = useAuth()
  const queryClient = useQueryClient()
  const { role, isLoading: roleLoading } = usePlatformRoleContext()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [profileLoading, setProfileLoading] = useState(false)
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [marketingConsentSaving, setMarketingConsentSaving] = useState(false)

  const { data: person } = useQuery({
    queryKey: ['person', user?.id],
    queryFn: async () => {
      if (!user?.id) return null
      const { data, error } = await supabase
        .from('people')
        .select('id, first_name, last_name, email, phone, marketing_consent')
        .eq('auth_user_id', user.id)
        .maybeSingle()
      if (error) return null
      return data
    },
    enabled: !!user?.id,
  })

  const meta = user?.user_metadata
  const existingFirst = meta?.first_name || person?.first_name || ''
  const existingLast = meta?.last_name || person?.last_name || ''
  const existingPhone = meta?.phone || person?.phone || ''
  useEffect(() => {
    setFirstName(existingFirst)
    setLastName(existingLast)
    setPhone(existingPhone)
  }, [existingFirst, existingLast, existingPhone])

  useEffect(() => {
    setMarketingConsent(!!person?.marketing_consent)
  }, [person?.marketing_consent])


  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setProfileLoading(true)
    try {
      const result = await updateUserMetadata({
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        phone: phone.trim() || undefined,
      })
      if (result.error) {
        toast.error(result.error)
      } else {
        if (person?.id) {
          const { error } = await supabase
            .from('people')
            .update({
              first_name: firstName.trim() || 'Unknown',
              last_name: lastName.trim() || 'User',
              phone: phone.trim() || null,
              marketing_consent: marketingConsent,
            })
            .eq('id', person.id)
          if (error) toast.error('Profile updated, but CRM sync failed')
        }
        toast.success('Profile updated')
        setIsEditingProfile(false)
        queryClient.invalidateQueries({ queryKey: ['person', user?.id] })
      }
    } finally {
      setProfileLoading(false)
    }
  }

  const handleMarketingConsentChange = async (checked: boolean) => {
    setMarketingConsent(checked)
    if (!person?.id) return
    setMarketingConsentSaving(true)
    try {
      const { error } = await supabase
        .from('people')
        .update({ marketing_consent: checked })
        .eq('id', person.id)
      if (error) {
        toast.error('Failed to update preference')
        setMarketingConsent(!checked)
      } else {
        queryClient.invalidateQueries({ queryKey: ['person', user?.id] })
      }
    } finally {
      setMarketingConsentSaving(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    setPasswordLoading(true)
    try {
      const result = await updatePassword(newPassword)
      if (result.error) {
        toast.error(result.error)
      } else {
        toast.success('Password updated successfully')
        setNewPassword('')
        setConfirmPassword('')
      }
    } finally {
      setPasswordLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-white">Settings</h1>
        <p className="text-white/60 text-sm mt-1">Manage your account and preferences.</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="rounded-xl border border-white/15 bg-white/5 p-6 backdrop-blur-sm space-y-6"
      >
        <div>
          <h2 className="text-sm font-heading uppercase tracking-wide text-white/70">Account</h2>
          <dl className="mt-4 space-y-4">
            <div>
              <dt className="text-xs text-white/50">Account type</dt>
              <dd className="text-white font-medium mt-0.5">
                {roleLoading ? (
                  <span className="inline-flex items-center gap-1.5 text-white/70">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading…
                  </span>
                ) : (
                  getAccountTypeLabel(role ?? null)
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-white/50">Email</dt>
              <dd className="text-white font-medium mt-0.5">{user?.email || '—'}</dd>
            </div>
          </dl>
        </div>

        <div className="pt-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-heading uppercase tracking-wide text-white/70">Profile</h2>
            {!isEditingProfile && (existingFirst || existingLast || existingPhone) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingProfile(true)}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
            )}
          </div>
          {(isEditingProfile || !existingFirst || !existingLast || !existingPhone) ? (
            <form onSubmit={handleSaveProfile} className="mt-4 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="first-name" className="text-white/80">
                    First name
                  </Label>
                  <Input
                    id="first-name"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    disabled={profileLoading}
                  />
                </div>
                <div>
                  <Label htmlFor="last-name" className="text-white/80">
                    Last name
                  </Label>
                  <Input
                    id="last-name"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    disabled={profileLoading}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="phone" className="text-white/80">
                  Phone
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone number"
                  className="mt-1.5 max-w-xs bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  disabled={profileLoading}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={profileLoading}
                  className="bg-white text-accent hover:bg-white/90"
                >
                  {profileLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving…
                    </>
                  ) : (
                    'Save profile'
                  )}
                </Button>
                {isEditingProfile && (existingFirst || existingLast || existingPhone) && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setIsEditingProfile(false)
                      setFirstName(existingFirst)
                      setLastName(existingLast)
                      setPhone(existingPhone)
                    }}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          ) : (
            <dl className="mt-4 space-y-4">
              <div>
                <dt className="text-xs text-white/50">Name</dt>
                <dd className="text-white font-medium mt-0.5">
                  {[existingFirst, existingLast].filter(Boolean).join(' ') || '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-white/50">Phone</dt>
                <dd className="text-white font-medium mt-0.5">{existingPhone || '—'}</dd>
              </div>
            </dl>
          )}
        </div>

        <div className="pt-4 border-t border-white/10">
          <h2 className="text-sm font-heading uppercase tracking-wide text-white/70">Preferences</h2>
          <label
            className={`mt-4 flex items-center gap-3 cursor-pointer group ${(marketingConsentSaving || !person?.id) ? 'opacity-60 pointer-events-none' : ''}`}
          >
            <span className="relative flex shrink-0">
              <input
                id="marketing-consent"
                type="checkbox"
                checked={marketingConsent}
                onChange={(e) => handleMarketingConsentChange(e.target.checked)}
                disabled={marketingConsentSaving || !person?.id}
                className="peer sr-only"
              />
              <span className={`flex h-5 w-5 items-center justify-center rounded border-2 transition-all peer-checked:border-white peer-checked:bg-white peer-focus:border-white/60 group-hover:border-white/60 ${marketingConsent ? 'border-white bg-white' : 'border-white/40 bg-white/5'}`}>
                {marketingConsentSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin text-accent" />
                ) : marketingConsent ? (
                  <Check className="h-3 w-3 text-accent stroke-[3] animate-check-pop" />
                ) : null}
              </span>
            </span>
            <span className="text-white/90 text-sm leading-snug">
              I’d like to receive marketing and product updates via email.
            </span>
          </label>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="rounded-xl border border-white/15 bg-white/5 p-6 backdrop-blur-sm"
      >
        <h2 className="text-sm font-heading uppercase tracking-wide text-white/70">Change password</h2>
        <form onSubmit={handleChangePassword} className="mt-4 space-y-4 max-w-sm">
          <div>
            <Label htmlFor="new-password" className="text-white/80">
              New password
            </Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              minLength={6}
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={passwordLoading}
            />
          </div>
          <div>
            <Label htmlFor="confirm-password" className="text-white/80">
              Confirm new password
            </Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              minLength={6}
              className="mt-1.5 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              disabled={passwordLoading}
            />
          </div>
          <Button
            type="submit"
            disabled={passwordLoading || !newPassword || !confirmPassword}
            className="bg-white text-accent hover:bg-white/90"
          >
            {passwordLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Updating…
              </>
            ) : (
              'Update password'
            )}
          </Button>
        </form>
      </motion.div>
    </div>
  )
}
